import { Code2 } from 'lucide-react';
import type { Source } from '@/data/fixtures';
import { useSourceText } from '@/components/features/learning/sources/useSourceText';
import {
  ViewerLoading,
  ViewerError,
} from '@/components/features/learning/sources/viewers/shell/ViewerStates';
import { ViewerShell } from '@/components/features/learning/sources/viewers/shell/ViewerShell';

interface HTMLViewerProps {
  source: Source;
}

/**
 * Renders user-uploaded `.html` files in a sandboxed iframe via `srcdoc`.
 *
 * Sandbox attributes: NO `allow-scripts`, NO `allow-same-origin`. This
 * neutralizes any embedded JS / cross-origin fetches in the uploaded
 * markup so a malicious or noisy source can't reach app cookies, the
 * presigned URL, or the parent page's storage.
 */
export function HTMLViewer({ source }: HTMLViewerProps) {
  const { text, isLoading, error } = useSourceText(source.id);

  if (isLoading) return <ViewerLoading />;
  if (error)
    return <ViewerError message="Couldn't load file" detail={error.message} />;

  return (
    <ViewerShell
      sourceId={source.id}
      fullscreen
      icon={Code2}
      label='HTML'
      tone='lesson'
    >
      <iframe
        title={source.name}
        srcDoc={text || ''}
        sandbox=''
        className='h-full w-full border-0 bg-white'
      />
    </ViewerShell>
  );
}
